import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, DollarSign, CheckCircle, XCircle, Clock, Eye, Instagram, Globe, ExternalLink, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminAffiliatesPage = () => {
  const [activeTab, setActiveTab] = useState('affiliates');
  const [affiliates, setAffiliates] = useState([]);
  const [expandedAffiliate, setExpandedAffiliate] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const fetchData = async () => {
    try {
      const [affRes, payRes, postRes] = await Promise.all([
        axios.get(`${API}/admin/affiliates`, { withCredentials: true }),
        axios.get(`${API}/admin/payout-requests`, { withCredentials: true }),
        axios.get(`${API}/admin/affiliate-posts`, { withCredentials: true })
      ]);
      setAffiliates(affRes.data);
      setPayouts(payRes.data);
      setPosts(postRes.data.posts || []);
      setCompliance(postRes.data.compliance || []);
    } catch (err) {
      toast.error('Failed to load affiliate data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handlePayoutAction = async (payoutId, status) => {
    setProcessing(payoutId);
    try {
      const { data } = await axios.put(`${API}/admin/payout-requests/${payoutId}`, { status }, { withCredentials: true });
      toast.success(data.message || `Payout ${status}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${status} payout`);
    } finally {
      setProcessing(null);
    }
  };

  const handlePostAction = async (postId, status) => {
    setProcessing(postId);
    try {
      await axios.put(`${API}/admin/affiliate-posts/${postId}`, { status }, { withCredentials: true });
      toast.success(`Post ${status}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setProcessing(null);
    }
  };

  const [reminderLoading, setReminderLoading] = useState(false);
  const handleSendReminders = async () => {
    if (!window.confirm('Send weekly compliance reminder emails to all affiliates with 0 posts this week?')) return;
    setReminderLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/send-compliance-reminders`, {}, { withCredentials: true });
      toast.success(data.message || 'Reminders sent');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setReminderLoading(false);
    }
  };

  const pendingPayouts = payouts.filter(p => p.status === 'pending');
  const totalPending = pendingPayouts.reduce((sum, p) => sum + p.amount, 0);

  const pendingAffiliates = affiliates.filter(a => a.status === 'pending');

  const handleApproveAffiliate = async (id) => {
    if (!window.confirm('Approve this affiliate? A welcome email with their referral code and Stripe payout setup link will be sent.')) return;
    setProcessing(id);
    try {
      const { data } = await axios.post(`${API}/admin/affiliates/${id}/approve`, {}, { withCredentials: true });
      if (data.email_sent) {
        toast.success(`Affiliate approved — welcome email sent to ${data.email_to}${data.email_id ? ` (Resend id: ${data.email_id})` : ''}`, { duration: 9000 });
      } else {
        toast.warning(`Affiliate approved, but the welcome email FAILED to send to ${data.email_to}. Check that your domain is verified in Resend.`, { duration: 9000 });
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleResendWelcome = async (id) => {
    setProcessing(id);
    try {
      const { data } = await axios.post(`${API}/admin/affiliates/${id}/resend-welcome`, {}, { withCredentials: true });
      toast.success(`${data.message}${data.email_id ? ` (Resend id: ${data.email_id})` : ''}`, { duration: 9000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend welcome email', { duration: 9000 });
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectAffiliate = async (id) => {
    const reason = window.prompt('Optional — provide a short reason (shown to the affiliate in the rejection email):');
    if (reason === null) return; // user cancelled
    setProcessing(id);
    try {
      await axios.post(`${API}/admin/affiliates/${id}/reject`, { reason }, { withCredentials: true });
      toast.success('Affiliate rejected');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="admin-affiliates-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Link to="/admin" className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-6 transition-colors" data-testid="back-to-admin">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </Link>

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Affiliate Management
          </h1>
          {pendingPayouts.length > 0 && (
            <div className="flex items-center space-x-2 bg-yellow-500/10 text-yellow-400 px-4 py-2 rounded-lg text-sm" data-testid="pending-payouts-badge">
              <Clock className="w-4 h-4" />
              <span>{pendingPayouts.length} pending payout{pendingPayouts.length > 1 ? 's' : ''} (${totalPending.toFixed(2)})</span>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-5">
            <Users className="w-5 h-5 text-[#00F0FF] mb-2" />
            <p className="text-2xl font-bold text-white">{affiliates.length}</p>
            <p className="text-[#A1A7B5] text-sm">Total Affiliates</p>
          </div>
          <div className="glass-card p-5">
            <DollarSign className="w-5 h-5 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-white">${affiliates.reduce((s, a) => s + (a.total_earned || 0), 0).toFixed(2)}</p>
            <p className="text-[#A1A7B5] text-sm">Total Commissions</p>
          </div>
          <div className="glass-card p-5">
            <Clock className="w-5 h-5 text-yellow-400 mb-2" />
            <p className="text-2xl font-bold text-white">${totalPending.toFixed(2)}</p>
            <p className="text-[#A1A7B5] text-sm">Pending Payouts</p>
          </div>
          <div className="glass-card p-5">
            <CheckCircle className="w-5 h-5 text-purple-400 mb-2" />
            <p className="text-2xl font-bold text-white">{affiliates.reduce((s, a) => s + (a.total_sales || 0), 0)}</p>
            <p className="text-[#A1A7B5] text-sm">Total Referral Sales</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex mb-6 bg-[#0C0E14] rounded-lg p-1 border border-[#1E2532] overflow-x-auto">
          <button onClick={() => setActiveTab('pending')} className={`flex-1 min-w-max py-2.5 px-4 text-sm font-medium rounded-md transition-colors ${activeTab === 'pending' ? 'bg-[#00F0FF] text-black' : 'text-[#A1A7B5] hover:text-white'}`} data-testid="tab-pending">
            Pending ({pendingAffiliates.length})
            {pendingAffiliates.length > 0 && activeTab !== 'pending' && (
              <span className="ml-2 bg-yellow-500 text-black text-xs px-1.5 py-0.5 rounded-full">{pendingAffiliates.length}</span>
            )}
          </button>
          <button onClick={() => setActiveTab('affiliates')} className={`flex-1 min-w-max py-2.5 px-4 text-sm font-medium rounded-md transition-colors ${activeTab === 'affiliates' ? 'bg-[#00F0FF] text-black' : 'text-[#A1A7B5] hover:text-white'}`} data-testid="tab-affiliates">
            Affiliates ({affiliates.length})
          </button>
          <button onClick={() => setActiveTab('activity')} className={`flex-1 min-w-max py-2.5 px-4 text-sm font-medium rounded-md transition-colors ${activeTab === 'activity' ? 'bg-[#00F0FF] text-black' : 'text-[#A1A7B5] hover:text-white'}`} data-testid="tab-activity">
            Activity ({posts.length})
            {compliance.filter(c => !c.compliant).length > 0 && (
              <span className="ml-2 bg-yellow-500 text-black text-xs px-1.5 py-0.5 rounded-full">{compliance.filter(c => !c.compliant).length}</span>
            )}
          </button>
          <button onClick={() => setActiveTab('payouts')} className={`flex-1 min-w-max py-2.5 px-4 text-sm font-medium rounded-md transition-colors ${activeTab === 'payouts' ? 'bg-[#00F0FF] text-black' : 'text-[#A1A7B5] hover:text-white'}`} data-testid="tab-payouts">
            Payouts ({payouts.length})
            {pendingPayouts.length > 0 && (
              <span className="ml-2 bg-yellow-500 text-black text-xs px-1.5 py-0.5 rounded-full">{pendingPayouts.length}</span>
            )}
          </button>
        </div>

        {/* Pending Applications Tab */}
        {activeTab === 'pending' && (
          <div className="glass-card overflow-hidden" data-testid="pending-panel">
            <div className="p-6 border-b border-[#1E2532]">
              <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Pending Applications</h3>
              <p className="text-[#A1A7B5] text-sm mt-1">New affiliates awaiting your review. Approving sends them a welcome email with their referral code and payout/tax setup link.</p>
            </div>
            {pendingAffiliates.length === 0 ? (
              <div className="p-10 text-center text-[#A1A7B5]">No pending applications</div>
            ) : (
              <div className="divide-y divide-[#1E2532]">
                {pendingAffiliates.map((aff) => (
                  <div key={aff.id} className="p-5" data-testid={`pending-row-${aff.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[240px]">
                        <p className="text-white font-semibold">{aff.name}</p>
                        <p className="text-[#A1A7B5] text-sm">{aff.email}</p>
                        <p className="text-[#6B7280] text-xs mt-1">Applied {aff.created_at ? new Date(aff.created_at).toLocaleDateString() : '—'} &middot; Payment: <span className="capitalize">{aff.payment_method || '—'}</span></p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          onClick={() => handleApproveAffiliate(aff.id)}
                          disabled={processing === aff.id}
                          className="bg-green-500/20 hover:bg-green-500/30 text-green-400 text-xs px-3 py-1.5"
                          data-testid={`approve-affiliate-${aff.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Approve &amp; Send Welcome
                        </Button>
                        <Button
                          onClick={() => handleRejectAffiliate(aff.id)}
                          disabled={processing === aff.id}
                          className="bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs px-3 py-1.5"
                          data-testid={`reject-affiliate-${aff.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>

                    {/* Platforms + handles — helps admin vet the application */}
                    {(aff.platforms?.length > 0 || aff.audience_size) && (
                      <div className="mt-4 pt-4 border-t border-[#1E2532] flex flex-wrap gap-x-6 gap-y-3 text-xs">
                        {aff.platforms?.length > 0 && (
                          <div>
                            <p className="text-[#6B7280] uppercase tracking-wider mb-1.5">Platforms</p>
                            <div className="flex flex-wrap gap-1.5">
                              {aff.platforms.map((p) => (
                                <span key={p} className="bg-[#00F0FF]/10 text-[#00F0FF] px-2 py-0.5 rounded capitalize">{p}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {aff.audience_size && (
                          <div>
                            <p className="text-[#6B7280] uppercase tracking-wider mb-1.5">Audience</p>
                            <p className="text-white">{aff.audience_size}</p>
                          </div>
                        )}
                        {aff.social_handles && Object.keys(aff.social_handles).length > 0 && (
                          <div className="flex-1 min-w-[200px]">
                            <p className="text-[#6B7280] uppercase tracking-wider mb-1.5">Handles</p>
                            <div className="space-y-0.5">
                              {Object.entries(aff.social_handles).filter(([, v]) => v).map(([platform, handle]) => {
                                const urls = {
                                  instagram: `https://instagram.com/${handle.replace(/^@/, '')}`,
                                  tiktok: `https://tiktok.com/@${handle.replace(/^@/, '')}`,
                                  twitter: `https://x.com/${handle.replace(/^@/, '')}`,
                                  youtube: handle.startsWith('http') ? handle : `https://youtube.com/${handle.replace(/^@/, '@')}`,
                                };
                                return (
                                  <a key={platform} href={urls[platform] || '#'} target="_blank" rel="noopener noreferrer" className="block text-[#00F0FF] hover:underline text-xs" data-testid={`handle-link-${platform}-${aff.id}`}>
                                    <span className="text-[#6B7280] capitalize mr-1">{platform}:</span>{handle}
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {aff.promotion_plan && (
                      <div className="mt-3 pt-3 border-t border-[#1E2532] text-xs">
                        <p className="text-[#6B7280] uppercase tracking-wider mb-1">Promotion plan</p>
                        <p className="text-[#A1A7B5] leading-relaxed italic">&ldquo;{aff.promotion_plan}&rdquo;</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Affiliates Tab */}
        {activeTab === 'affiliates' && (
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#A1A7B5] border-b border-[#1E2532]">
                    <th className="p-4">Name</th>
                    <th className="p-4">Email</th>
                    <th className="p-4">Ref Code</th>
                    <th className="p-4">Platforms</th>
                    <th className="p-4">Clicks</th>
                    <th className="p-4">Sales</th>
                    <th className="p-4">Earned</th>
                    <th className="p-4">Balance</th>
                    <th className="p-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E2532]">
                  {affiliates.length === 0 ? (
                    <tr><td colSpan={9} className="p-8 text-center text-[#A1A7B5]">No affiliates yet</td></tr>
                  ) : (
                    affiliates.map((aff) => (
                      <React.Fragment key={aff.id}>
                        <tr
                          className="text-white hover:bg-[#111621] transition-colors cursor-pointer"
                          onClick={() => setExpandedAffiliate(expandedAffiliate === aff.id ? null : aff.id)}
                          data-testid={`affiliate-row-${aff.id}`}
                        >
                          <td className="p-4 font-medium">{aff.name}</td>
                          <td className="p-4 text-[#A1A7B5]">{aff.email}</td>
                          <td className="p-4 font-mono text-xs text-[#00F0FF]">{aff.ref_code}</td>
                          <td className="p-4">
                            {aff.platforms?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {aff.platforms.slice(0, 3).map(p => (
                                  <span key={p} className="bg-[#00F0FF]/10 text-[#00F0FF] text-[10px] px-1.5 py-0.5 rounded capitalize">{p}</span>
                                ))}
                                {aff.platforms.length > 3 && <span className="text-[#A1A7B5] text-[10px]">+{aff.platforms.length - 3}</span>}
                              </div>
                            ) : <span className="text-[#6B7280] text-xs">—</span>}
                          </td>
                          <td className="p-4">{aff.total_clicks || 0}</td>
                          <td className="p-4">{aff.total_sales || 0}</td>
                          <td className="p-4 text-green-400">${(aff.total_earned || 0).toFixed(2)}</td>
                          <td className="p-4 text-purple-400">${(aff.balance || 0).toFixed(2)}</td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${aff.status === 'active' ? 'bg-green-500/20 text-green-400' : aff.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                              {aff.status}
                            </span>
                            {aff.status === 'pending' && (
                              <Button
                                onClick={(e) => { e.stopPropagation(); handleApproveAffiliate(aff.id); }}
                                disabled={processing === aff.id}
                                className="mt-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 text-[11px] px-2.5 py-1 h-auto flex items-center w-max"
                                data-testid={`approve-affiliate-inline-${aff.id}`}
                                title="Approve this applicant and email them their referral code + Stripe payout/onboarding link"
                              >
                                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                {processing === aff.id ? 'Approving…' : 'Approve & Onboard'}
                              </Button>
                            )}
                          </td>
                        </tr>
                        {expandedAffiliate === aff.id && (
                          <tr className="bg-[#0A0D12]" data-testid={`affiliate-expanded-${aff.id}`}>
                            <td colSpan={9} className="p-4 pt-2 pb-5">
                              <div className="flex flex-wrap gap-x-8 gap-y-4 text-xs">
                                <div>
                                  <p className="text-[#6B7280] uppercase tracking-wider mb-1.5">Audience Size</p>
                                  <p className="text-white">{aff.audience_size || '—'}</p>
                                </div>
                                <div>
                                  <p className="text-[#6B7280] uppercase tracking-wider mb-1.5">Payment</p>
                                  <p className="text-white capitalize">{aff.payment_method || '—'}{aff.payment_details ? ` · ${aff.payment_details}` : ''}</p>
                                </div>
                                {aff.social_handles && Object.entries(aff.social_handles).filter(([, v]) => v).length > 0 && (
                                  <div className="flex-1 min-w-[260px]">
                                    <p className="text-[#6B7280] uppercase tracking-wider mb-1.5">Social Handles</p>
                                    <div className="space-y-1">
                                      {Object.entries(aff.social_handles).filter(([, v]) => v).map(([platform, handle]) => {
                                        const urls = {
                                          instagram: `https://instagram.com/${handle.replace(/^@/, '')}`,
                                          tiktok: `https://tiktok.com/@${handle.replace(/^@/, '')}`,
                                          twitter: `https://x.com/${handle.replace(/^@/, '')}`,
                                          youtube: handle.startsWith('http') ? handle : `https://youtube.com/${handle.replace(/^@/, '@')}`,
                                        };
                                        return (
                                          <a key={platform} href={urls[platform] || '#'} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-[#00F0FF] hover:underline mr-4" data-testid={`active-handle-${platform}-${aff.id}`}>
                                            <span className="text-[#6B7280] capitalize">{platform}:</span> {handle}
                                            <ExternalLink className="w-3 h-3" />
                                          </a>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {aff.promotion_plan && (
                                  <div className="basis-full">
                                    <p className="text-[#6B7280] uppercase tracking-wider mb-1.5">Promotion Plan</p>
                                    <p className="text-[#A1A7B5] italic leading-relaxed">&ldquo;{aff.promotion_plan}&rdquo;</p>
                                  </div>
                                )}
                                <div className="basis-full flex items-center gap-3 pt-1">
                                  <Button
                                    onClick={(e) => { e.stopPropagation(); handleResendWelcome(aff.id); }}
                                    disabled={processing === aff.id}
                                    className="bg-[#00F0FF]/15 hover:bg-[#00F0FF]/25 text-[#00F0FF] text-xs px-3 py-1.5 h-auto flex items-center"
                                    data-testid={`resend-welcome-${aff.id}`}
                                    title="Re-send the welcome email (referral code + onboarding link) to this affiliate"
                                  >
                                    <Mail className="w-3.5 h-3.5 mr-1.5" />
                                    {processing === aff.id ? 'Sending…' : 'Resend Welcome Email'}
                                  </Button>
                                  <span className="text-[#6B7280] text-xs">Sends to {aff.email}</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-6" data-testid="activity-panel">
            {/* Weekly Compliance Summary */}
            <div className="glass-card overflow-hidden">
              <div className="p-6 border-b border-[#1E2532] flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Weekly Compliance
                  </h3>
                  <p className="text-[#A1A7B5] text-sm mt-1">Affiliates are required to post at least 1x per week. Reminders auto-send every Sunday 15:00 UTC.</p>
                </div>
                <Button
                  onClick={handleSendReminders}
                  disabled={reminderLoading}
                  className="bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 text-[#00F0FF] text-sm px-4 py-2 flex items-center gap-2"
                  data-testid="send-reminders-btn"
                >
                  <Mail className="w-4 h-4" />
                  {reminderLoading ? 'Sending…' : 'Send Reminders Now'}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#A1A7B5] border-b border-[#1E2532]">
                      <th className="p-4">Affiliate</th>
                      <th className="p-4">Email</th>
                      <th className="p-4">Posts This Week</th>
                      <th className="p-4">Total Posts</th>
                      <th className="p-4">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1E2532]">
                    {compliance.length === 0 ? (
                      <tr><td colSpan="5" className="p-8 text-center text-[#A1A7B5]">No active affiliates yet</td></tr>
                    ) : (
                      compliance.map((c) => (
                        <tr key={c.affiliate_id} className="text-white hover:bg-[#111621] transition-colors" data-testid={`compliance-row-${c.affiliate_id}`}>
                          <td className="p-4 font-medium">{c.name}</td>
                          <td className="p-4 text-[#A1A7B5]">{c.email}</td>
                          <td className="p-4">
                            <span className={c.posts_this_week >= 1 ? 'text-green-400' : 'text-yellow-400'}>
                              {c.posts_this_week} / 1
                            </span>
                          </td>
                          <td className="p-4 text-[#A1A7B5]">{c.total_posts}</td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${c.compliant ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {c.compliant ? 'Compliant' : 'Missing post'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Posts Queue */}
            <div className="glass-card overflow-hidden">
              <div className="p-6 border-b border-[#1E2532]">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Submitted Posts
                </h3>
                <p className="text-[#A1A7B5] text-sm mt-1">Review, approve or reject posts submitted by affiliates</p>
              </div>
              {posts.length === 0 ? (
                <div className="p-8 text-center text-[#A1A7B5]">No posts submitted yet</div>
              ) : (
                <div className="divide-y divide-[#1E2532]">
                  {posts.map((post) => {
                    const platformIcon = post.platform === 'instagram' ? Instagram : Globe;
                    const PlatformIcon = platformIcon;
                    return (
                      <div key={post.id} className="p-4 flex items-start gap-4" data-testid={`admin-post-${post.id}`}>
                        <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center flex-shrink-0">
                          <PlatformIcon className="w-5 h-5 text-[#00F0FF]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-medium">{post.affiliate_name}</span>
                            <span className="text-[#A1A7B5] text-xs">&middot; {post.affiliate_email}</span>
                            <span className="text-[#A1A7B5] text-xs">&middot; {new Date(post.created_at).toLocaleDateString()}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${post.status === 'approved' ? 'bg-green-500/20 text-green-400' : post.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {post.status}
                            </span>
                          </div>
                          <a href={post.post_url} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] text-sm hover:underline break-all flex items-center gap-1 mt-1" data-testid={`post-url-${post.id}`}>
                            {post.post_url}
                            <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                          </a>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-[#A1A7B5] text-xs capitalize">Platform: {post.platform}</span>
                            {post.notes && <span className="text-[#A1A7B5] text-xs">Notes: {post.notes}</span>}
                          </div>
                        </div>
                        {post.status === 'pending' && (
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              onClick={() => handlePostAction(post.id, 'approved')}
                              disabled={processing === post.id}
                              className="bg-green-500/20 hover:bg-green-500/30 text-green-400 text-xs px-3 py-1.5"
                              data-testid={`approve-post-${post.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button
                              onClick={() => handlePostAction(post.id, 'rejected')}
                              disabled={processing === post.id}
                              className="bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs px-3 py-1.5"
                              data-testid={`reject-post-${post.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#A1A7B5] border-b border-[#1E2532]">
                    <th className="p-4">Affiliate</th>
                    <th className="p-4">Amount</th>
                    <th className="p-4">Method</th>
                    <th className="p-4">Details</th>
                    <th className="p-4">Date</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E2532]">
                  {payouts.length === 0 ? (
                    <tr><td colSpan={7} className="p-8 text-center text-[#A1A7B5]">No payout requests yet</td></tr>
                  ) : (
                    payouts.map((payout) => (
                      <tr key={payout.id} className="text-white hover:bg-[#111621] transition-colors" data-testid={`payout-row-${payout.id}`}>
                        <td className="p-4">
                          <p className="font-medium">{payout.affiliate_name}</p>
                          <p className="text-[#A1A7B5] text-xs">{payout.affiliate_email}</p>
                        </td>
                        <td className="p-4 font-semibold text-[#00F0FF]">${payout.amount.toFixed(2)}</td>
                        <td className="p-4 capitalize">{payout.payment_method}</td>
                        <td className="p-4 text-[#A1A7B5] text-xs max-w-[160px] truncate">{payout.payment_details}</td>
                        <td className="p-4 text-[#A1A7B5] text-xs">{new Date(payout.created_at).toLocaleDateString()}</td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                            payout.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            payout.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {payout.status}
                          </span>
                          {payout.stripe_transfer_id && (
                            <a href={`https://dashboard.stripe.com/connect/transfers/${payout.stripe_transfer_id}`} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-[#00F0FF] hover:underline mt-1 font-mono">via Stripe ↗</a>
                          )}
                          {payout.payout_method_used === 'manual' && payout.status === 'approved' && (
                            <span className="block text-[10px] text-[#A1A7B5] mt-1">paid manually</span>
                          )}
                        </td>
                        <td className="p-4">
                          {payout.status === 'pending' ? (() => {
                            const aff = affiliates.find(a => a.id === payout.affiliate_id || a.email === payout.affiliate_email);
                            const stripeReady = aff?.stripe_account_id && aff?.stripe_payouts_enabled;
                            return (
                              <div className="flex flex-col gap-1.5 items-start">
                                {stripeReady ? (
                                  <Button
                                    size="sm"
                                    onClick={() => handlePayoutAction(payout.id, 'approved')}
                                    disabled={processing === payout.id}
                                    className="bg-[#00F0FF]/20 hover:bg-[#00F0FF]/30 text-[#00F0FF] text-xs px-3 py-1 h-auto w-full justify-center"
                                    data-testid={`pay-stripe-${payout.id}`}
                                    title="Send money automatically via Stripe → deposits in affiliate's bank in ~2 business days"
                                  >
                                    <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                    {processing === payout.id ? 'Sending…' : `Pay $${payout.amount.toFixed(2)} via Stripe`}
                                  </Button>
                                ) : (
                                  <div className="w-full bg-yellow-500/10 border border-yellow-500/30 rounded p-2 text-[10px] text-yellow-300" data-testid={`payout-blocked-${payout.id}`}>
                                    <p className="font-semibold mb-1">Can&apos;t pay — Stripe onboarding incomplete</p>
                                    <p className="text-[9px] opacity-80">Ask the affiliate to complete payout & tax setup at /affiliate/tax-info before you can approve this request.</p>
                                  </div>
                                )}
                                <Button
                                  size="sm"
                                  onClick={() => handlePayoutAction(payout.id, 'rejected')}
                                  disabled={processing === payout.id}
                                  className="bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs px-3 py-1 h-auto w-full justify-center"
                                  data-testid={`reject-payout-${payout.id}`}
                                >
                                  <XCircle className="w-3.5 h-3.5 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            );
                          })() : (
                            <span className="text-xs text-[#A1A7B5]">
                              {payout.updated_at ? new Date(payout.updated_at).toLocaleDateString() : '-'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAffiliatesPage;
